import {
  createContext,
  FunctionComponent,
  useEffect,
  useRef,
  useState,
} from 'react';
import axios from 'axios';
import { find } from 'lodash';

export const PricingContext = createContext<IPricingContextValue>(
  {} as IPricingContextValue
);

export const PricingContextProvider: FunctionComponent<{
  initialCalculations?: ICalculation[];
}> = ({ children, initialCalculations }) => {
  const [calculations, setCalculations] = useState<ICalculation[]>(
    initialCalculations || []
  );

  const isFetchingRef = useRef(false);
  const [isFetchingCalculation, setIsFetchingCalculation] = useState(false);
  const [termLength, setTermLength] = useState(ETermLength.MONTH);
  const [gateway, setGateway] = useState(EGatewaySlug.VIRTUAL);
  const [apiCreditsQuantity, setApiCreditsQuantity] = useState(million(5));
  const [buildingQuantity, setBuildingQuantity] = useState(1);

  async function fetchCalculations(
    items: { slug: string; quantity: number }[]
  ) {
    const itemsToFetch = items.filter(
      ({ slug, quantity }) => !find(calculations, { slug, quantity })
    );

    if (!!isFetchingRef.current || !itemsToFetch.length) {
      return;
    }

    isFetchingRef.current = true;
    setIsFetchingCalculation(true);

    const fetchedCalcs = await getCalculations(itemsToFetch);

    setCalculations([...calculations, ...fetchedCalcs]);

    isFetchingRef.current = false;
    setIsFetchingCalculation(false);
  }

  function getPlanPrice(slug: EPlanSlug) {
    const c = find(calculations, { slug, quantity: buildingQuantity });
    return normalizePrice(c?.unitPrice);
  }

  function getGatewayPrice() {
    const c = find(calculations, { slug: gateway, quantity: buildingQuantity });
    return normalizePrice(c?.unitPrice);
  }

  function getApiCreditsPrice(slug?: EApiPackageSlug) {
    const c = find(calculations, {
      slug: slug || getBestApiPackageSlugForQuantity(apiCreditsQuantity),
      quantity: apiCreditsQuantity,
    });

    return {
      fixedPrice: normalizePrice(c?.fixedPrice),
      overagePrice: normalizePrice(c?.overagePrice),
    };
  }

  useEffect(() => {
    fetchCalculations([
      { slug: EPlanSlug.PRO, quantity: buildingQuantity },
      { slug: EGatewaySlug.PHYSICAL, quantity: buildingQuantity },
    ]);
  }, [buildingQuantity]);

  useEffect(() => {
    fetchCalculations([
      { slug: EApiPackageSlug.PAY_AS_YOU_GO, quantity: apiCreditsQuantity },
      {
        slug: getBestApiPackageSlugForQuantity(apiCreditsQuantity),
        quantity: apiCreditsQuantity,
      },
    ]);
  }, [apiCreditsQuantity]);

  return (
    <PricingContext.Provider
      value={{
        isFetchingCalculation,
        termLength,
        setTermLength,
        gateway,
        setGateway,
        apiCreditsQuantity,
        setApiCreditsQuantity,
        buildingQuantity,
        setBuildingQuantity,
        getPlanPrice,
        getGatewayPrice,
        getApiCreditsPrice,
      }}
    >
      {children}
    </PricingContext.Provider>
  );
};

export async function getCalculations(
  items: { slug: string; quantity: number }[]
) {
  try {
    const res = await axios.post('/api/calculations', items);

    return res.data as ICalculation[];
  } catch (e) {
    console.log(JSON.stringify(e, null, 2));
    return [];
  }
}

function getBestApiPackageSlugForQuantity(qty: number) {
  switch (true) {
    case qty >= million(50):
      return EApiPackageSlug.M50;
    case qty >= million(20):
      return EApiPackageSlug.M20;
    case qty >= million(10):
      return EApiPackageSlug.M10;
    case qty >= million(5):
      return EApiPackageSlug.M5;
    default:
      return EApiPackageSlug.PAY_AS_YOU_GO;
  }
}

function normalizePrice(n: any) {
  return (n || 0) / 100;
}

export function getPriceString(price: number, hideIfZero?: boolean) {
  if (!price && hideIfZero) {
    return '';
  }

  if (!price) {
    return 'FREE';
  }

  if (price < 1) {
    return '$' + parseFloat(price.toFixed(9));
  }

  return '$' + Number(price).toLocaleString('en');
}

export const million = (q: number) => q * 1000000;

export function numberToWord(n: number) {
  if (n >= 1000000) {
    return String(n / 1000000 + 'M');
  }

  if (n >= 1000) {
    return String(n / 1000 + 'M');
  }

  return n;
}

export function getSavingPercentage(firstPrice: number, secondPrice: number) {
  const discount = firstPrice - secondPrice;
  const percentage = Math.round((discount / firstPrice) * 100);

  if (percentage > 0) {
    return percentage + '%';
  }

  return '';
}

interface IPricingContextValue {
  isFetchingCalculation: boolean;
  termLength: ETermLength;
  gateway: EGatewaySlug;
  setGateway: (g: EGatewaySlug) => void;
  setTermLength: (t: ETermLength) => void;
  apiCreditsQuantity: number;
  setApiCreditsQuantity: (n: number) => void;
  buildingQuantity: number;
  setBuildingQuantity: (n: number) => void;
  getPlanPrice: (s: EPlanSlug) => number;
  getGatewayPrice: () => number;
  getApiCreditsPrice: (slug?: EApiPackageSlug) => {
    fixedPrice: number;
    overagePrice: number;
  };
}

export enum EPlanSlug {
  STARTER = 'plan_starter.platform',
  PRO = 'plan_pro.platform',
  ENTERPRISE = 'plan_enterprise.platform',
}

export enum EGatewaySlug {
  VIRTUAL = 'gateway.gateway_virtual',
  PHYSICAL = 'gateway.gateway_physical',
}

export enum EApiPackageSlug {
  PAY_AS_YOU_GO = 'plan_pro.api_package_pay_as_you_go',
  M5 = 'plan_pro.api_package_5m',
  M10 = 'plan_pro.api_package_10m',
  M20 = 'plan_pro.api_package_20m',
  M50 = 'plan_pro.api_package_50m',
}

export enum ETermLength {
  MONTH = 'month',
  YEAR = 'year',
}

export interface ICalculation {
  slug: string;
  quantity?: number;
  unitPrice?: number;
  fixedPrice?: number;
  overagePrice?: number;
}

export const InitialCalculationsRequest = [
  {
    slug: EPlanSlug.PRO,
    quantity: 1,
  },
  {
    slug: EGatewaySlug.VIRTUAL,
    quantity: 1,
  },
  {
    slug: EGatewaySlug.PHYSICAL,
    quantity: 1,
  },
  {
    slug: EApiPackageSlug.PAY_AS_YOU_GO,
    quantity: million(5),
  },
  {
    slug: EApiPackageSlug.M5,
    quantity: million(5),
  },
];
